import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { DomainStatusResolver } from './statusResolver';
import { buildResolvedAction } from './buildResolvedAction';
import { useActionHandlers, useAutoInvokeOverride, useResolveCorrectedDomains, useSiteInfo, useUiConfig } from './context';
import { toCorrectedDomain } from './utils';
import { usePremiumEssentialsContext } from '@wix/premium-fed-sdk/@premium-essentials';
import { CoreDomainSearchModule } from '@wix/core-domain-search';
import { useDomainSearchStatuses, useSuggestionStatuses } from './components/useStatuses';
import { DomainsApiClient } from '@wix/domains-serverless-interface/react-kit';
import { DomainSearchSuiteModule } from '../../domainSearchSuite.module';
export const useStatusActionResolver = () => {
  var _CoreDomainSearchModu, _accountParams$sites;
  const _DomainSearchSuiteMod = DomainSearchSuiteModule.SDK.domainSearchSuiteStore,
    useStatus = _DomainSearchSuiteMod.useStatus,
    useError = _DomainSearchSuiteMod.useError,
    setStatus = _DomainSearchSuiteMod.setStatus,
    setError = _DomainSearchSuiteMod.setError;
  const status = useStatus();
  const error = useError();
  const _useState = useState(false),
    loading = _useState[0],
    setLoading = _useState[1];
  const _usePremiumEssentials = usePremiumEssentialsContext(),
    httpClient = _usePremiumEssentials.httpClient,
    siteId = _usePremiumEssentials.siteId;
  const submittedDomain = CoreDomainSearchModule.SDK.domainInputStore.useSubmittedDomain();
  const submittedQuery = (_CoreDomainSearchModu = CoreDomainSearchModule.SDK.domainInputStore.useSubmittedFilters()) == null ? void 0 : _CoreDomainSearchModu.query;
  const resolveCorrectedDomains = useResolveCorrectedDomains();
  const domainToResolve = submittedDomain ?? (resolveCorrectedDomains ? toCorrectedDomain(submittedQuery) : undefined);
  const _useSiteInfo = useSiteInfo(),
    externalViewer = _useSiteInfo.externalViewer;
  const _useUiConfig = useUiConfig(),
    fullPage = _useUiConfig.fullPage,
    useUpgradePaywall = _useUiConfig.useUpgradePaywall,
    referralAdditionalInfo = _useUiConfig.referralAdditionalInfo,
    sameTab = _useUiConfig.sameTab;
  const _DomainsApiClient$hoo = DomainsApiClient.hooks.useDomainsAccount({}),
    accountParams = _DomainsApiClient$hoo.data;
  const hasZeroSites = ((accountParams == null || (_accountParams$sites = accountParams.sites) == null ? void 0 : _accountParams$sites.length) ?? 0) === 0;
  const domainStatusResolver = useMemo(() => new DomainStatusResolver(httpClient, {
    msid: siteId,
    externalViewer,
    hasZeroSites
  }), [httpClient, siteId, externalViewer, hasZeroSites]);
  const domainToResolveRef = useRef(domainToResolve);
  domainToResolveRef.current = domainToResolve;
  const resolveStatus = useCallback(async _ref => {
    let domainName = _ref.domainName;
    setLoading(true);
    setError(undefined);
    try {
      const result = await domainStatusResolver.resolveStatus({
        domainName
      });
      if (domainToResolveRef.current === domainName) {
        setStatus(result == null ? void 0 : result.status);
      }
    } catch (err) {
      if (domainToResolveRef.current === domainName) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error';
        setError(errorMessage);
        console.error('Error resolving domain status:', err);
      }
    } finally {
      setLoading(false);
    }
  }, [domainStatusResolver, setStatus, setError, setLoading]);
  const resolveActionForDomain = useCallback(async domain => {
    return domainStatusResolver.resolveStatus({
      domainName: domain,
      overrides: {
        purchaseResult: {
          status: 'fulfilled',
          value: {
            canPurchase: true
          }
        }
      }
    });
  }, [domainStatusResolver]);
  useEffect(() => {
    if (domainToResolve) {
      resolveStatus({
        domainName: domainToResolve
      });
    }
  }, [domainToResolve, resolveStatus, status]);
  return {
    status,
    loading,
    resolveActionForDomain,
    domainName: domainToResolve,
    error,
    externalViewer,
    fullPage,
    useUpgradePaywall,
    referralAdditionalInfo,
    sameTab,
    siteId: siteId ?? (accountParams == null ? void 0 : accountParams.msid),
    siteName: accountParams == null ? void 0 : accountParams.siteName
  };
};
export const useDomainAction = () => {
  const _useState2 = useState(false),
    loading = _useState2[0],
    setLoading = _useState2[1];
  const _useState3 = useState(null),
    error = _useState3[0],
    setError = _useState3[1];
  const executeAction = useCallback(async actionHandler => {
    setLoading(true);
    setError(null);
    try {
      if (!actionHandler) {
        return;
      }
      const result = await actionHandler();
      return result;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(errorMessage);
      console.error(`Error executing action:`, err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);
  return {
    executeAction,
    loading,
    error
  };
};
const useActionResolver = _ref2 => {
  let loading = _ref2.loading,
    status = _ref2.status,
    domainName = _ref2.domainName,
    msid = _ref2.msid,
    siteName = _ref2.siteName,
    externalViewer = _ref2.externalViewer,
    fullPage = _ref2.fullPage,
    useUpgradePaywall = _ref2.useUpgradePaywall,
    referralAdditionalInfo = _ref2.referralAdditionalInfo,
    sameTab = _ref2.sameTab;
  const action = useDomainAction();
  const statuses = useDomainSearchStatuses();
  const _DomainSearchSuiteMod2 = DomainSearchSuiteModule.internals.useTranslations(),
    t = _DomainSearchSuiteMod2.t;
  const _DomainSearchSuiteMod3 = DomainSearchSuiteModule.SDK.useStatusesCustomComponents(),
    statusesCustomComponents = _DomainSearchSuiteMod3.statusesCustomComponents;
  const _DomainSearchSuiteMod4 = DomainSearchSuiteModule.SDK.domainSearchSuiteStore,
    setIsInvokedAction = _DomainSearchSuiteMod4.setIsInvokedAction,
    useIsInvokedAction = _DomainSearchSuiteMod4.useIsInvokedAction;
  const actionHandlers = useActionHandlers();
  const autoInvokeOverride = useAutoInvokeOverride();
  const isInvokedAction = useIsInvokedAction();
  const commonProps = domainName && status ? {
    domainName,
    msid,
    siteName,
    externalViewer,
    fullPage,
    useUpgradePaywall,
    referralAdditionalInfo,
    sameTab,
    autoInvoke: autoInvokeOverride,
    t
  } : undefined;
  const _buildResolvedAction = buildResolvedAction({
      status,
      commonProps,
      statuses,
      actionHandlers,
      statusesCustomComponents
    }),
    resolvedAction = _buildResolvedAction.action,
    autoInvoke = _buildResolvedAction.autoInvoke,
    StatusComponent = _buildResolvedAction.StatusComponent;
  const actionStatus = resolvedAction !== undefined || StatusComponent ? {
    action: resolvedAction,
    ...(StatusComponent ? {
      StatusComponent
    } : {})
  } : undefined;
  useEffect(() => {
    if (autoInvoke && status && !isInvokedAction) {
      setIsInvokedAction(true);
      action.executeAction(resolvedAction);
    }
  }, [action, status, autoInvoke, resolvedAction, setIsInvokedAction, isInvokedAction]);
  return {
    actionStatus,
    autoInvoke,
    action: () => action.executeAction(actionStatus == null ? void 0 : actionStatus.action),
    status,
    domainName,
    externalViewer,
    loading
  };
};
export const useSearchActionResolver = () => {
  const _useStatusActionResol = useStatusActionResolver(),
    status = _useStatusActionResol.status,
    domainName = _useStatusActionResol.domainName,
    siteId = _useStatusActionResol.siteId,
    siteName = _useStatusActionResol.siteName,
    externalViewer = _useStatusActionResol.externalViewer,
    fullPage = _useStatusActionResol.fullPage,
    useUpgradePaywall = _useStatusActionResol.useUpgradePaywall,
    referralAdditionalInfo = _useStatusActionResol.referralAdditionalInfo,
    sameTab = _useStatusActionResol.sameTab,
    loading = _useStatusActionResol.loading;
  return useActionResolver({
    status,
    domainName,
    msid: siteId,
    siteName,
    externalViewer,
    fullPage,
    useUpgradePaywall,
    referralAdditionalInfo,
    sameTab,
    loading
  });
};
export const useSuggestionActionResolver = () => {
  const _useDomainAction = useDomainAction(),
    executeAction = _useDomainAction.executeAction;
  const _useStatusActionResol2 = useStatusActionResolver(),
    resolveActionForDomain = _useStatusActionResol2.resolveActionForDomain,
    externalViewer = _useStatusActionResol2.externalViewer,
    fullPage = _useStatusActionResol2.fullPage,
    useUpgradePaywall = _useStatusActionResol2.useUpgradePaywall,
    referralAdditionalInfo = _useStatusActionResol2.referralAdditionalInfo,
    sameTab = _useStatusActionResol2.sameTab,
    siteName = _useStatusActionResol2.siteName,
    siteId = _useStatusActionResol2.siteId;
  const statuses = useSuggestionStatuses();
  const _DomainSearchSuiteMod5 = DomainSearchSuiteModule.internals.useTranslations(),
    t = _DomainSearchSuiteMod5.t;
  const actionHandlers = useActionHandlers();
  const resolveActionForSuggestion = useCallback(async (domainName, isPremiumDomain) => {
    const _await$resolveActionF = await resolveActionForDomain(domainName),
      domainStatus = _await$resolveActionF.status;
    const _buildResolvedAction2 = buildResolvedAction({
        status: domainStatus,
        commonProps: {
          domainName,
          isPremiumDomain,
          msid: siteId,
          siteName,
          externalViewer,
          fullPage,
          useUpgradePaywall,
          referralAdditionalInfo,
          sameTab,
          t
        },
        statuses,
        actionHandlers
      }),
      resolvedAction = _buildResolvedAction2.action;
    if (!resolvedAction) {
      return {
        action: () => {},
        Status: undefined
      };
    }
    await executeAction(resolvedAction);
    return {
      action: () => {},
      Status: undefined
    };
  }, [resolveActionForDomain, executeAction, statuses, siteId, siteName, externalViewer, fullPage, useUpgradePaywall, referralAdditionalInfo, sameTab, actionHandlers, t]);
  return {
    resolveActionForSuggestion
  };
};
const useResolveDomainActionCore = () => {
  var _accountParams$sites2;
  const _usePremiumEssentials2 = usePremiumEssentialsContext(),
    httpClient = _usePremiumEssentials2.httpClient,
    siteId = _usePremiumEssentials2.siteId;
  const _useSiteInfo2 = useSiteInfo(),
    externalViewer = _useSiteInfo2.externalViewer;
  const _useUiConfig2 = useUiConfig(),
    fullPage = _useUiConfig2.fullPage,
    useUpgradePaywall = _useUiConfig2.useUpgradePaywall,
    referralAdditionalInfo = _useUiConfig2.referralAdditionalInfo,
    sameTab = _useUiConfig2.sameTab;
  const actionHandlers = useActionHandlers();
  const autoInvokeOverride = useAutoInvokeOverride();
  const statuses = useSuggestionStatuses();
  const _DomainSearchSuiteMod6 = DomainSearchSuiteModule.SDK.useStatusesCustomComponents(),
    statusesCustomComponents = _DomainSearchSuiteMod6.statusesCustomComponents;
  const _DomainSearchSuiteMod7 = DomainSearchSuiteModule.internals.useTranslations(),
    t = _DomainSearchSuiteMod7.t;
  const _DomainsApiClient$hoo2 = DomainsApiClient.hooks.useDomainsAccount({}),
    accountParams = _DomainsApiClient$hoo2.data;
  const currentMsid = siteId ?? (accountParams == null ? void 0 : accountParams.msid);
  const currentSiteName = accountParams == null ? void 0 : accountParams.siteName;
  const currentHasZeroSites = ((accountParams == null || (_accountParams$sites2 = accountParams.sites) == null ? void 0 : _accountParams$sites2.length) ?? 0) === 0;
  const inputsRef = useRef({
    httpClient,
    externalViewer,
    fullPage,
    useUpgradePaywall,
    referralAdditionalInfo,
    sameTab,
    actionHandlers,
    autoInvokeOverride,
    statuses,
    statusesCustomComponents,
    t,
    currentMsid,
    currentSiteName,
    currentHasZeroSites
  });
  inputsRef.current = {
    httpClient,
    externalViewer,
    fullPage,
    useUpgradePaywall,
    referralAdditionalInfo,
    sameTab,
    actionHandlers,
    autoInvokeOverride,
    statuses,
    statusesCustomComponents,
    t,
    currentMsid,
    currentSiteName,
    currentHasZeroSites
  };
  return useCallback(async (domainName, options) => {
    var _options$site, _options$site2;
    const _inputsRef$current = inputsRef.current,
      client = _inputsRef$current.httpClient,
      external = _inputsRef$current.externalViewer,
      isFullPage = _inputsRef$current.fullPage,
      paywall = _inputsRef$current.useUpgradePaywall,
      referral = _inputsRef$current.referralAdditionalInfo,
      selfTab = _inputsRef$current.sameTab,
      handlers = _inputsRef$current.actionHandlers,
      autoInvoke = _inputsRef$current.autoInvokeOverride,
      statusMap = _inputsRef$current.statuses,
      customComponents = _inputsRef$current.statusesCustomComponents,
      translate = _inputsRef$current.t,
      msidFallback = _inputsRef$current.currentMsid,
      siteNameFallback = _inputsRef$current.currentSiteName,
      hasZeroSites = _inputsRef$current.currentHasZeroSites;
    const selectedSiteId = options == null || (_options$site = options.site) == null ? void 0 : _options$site.siteId;
    const msid = selectedSiteId ?? msidFallback;
    const siteName = (options == null || (_options$site2 = options.site) == null ? void 0 : _options$site2.siteName) ?? siteNameFallback;
    const resolver = new DomainStatusResolver(client, {
      msid,
      externalViewer: external,
      hasZeroSites,
      msidIsSelectedSite: Boolean(selectedSiteId)
    });
    const _await$resolver$resol = await resolver.resolveStatus({
        domainName
      }),
      status = _await$resolver$resol.status;
    return buildResolvedAction({
      status,
      commonProps: {
        domainName,
        msid,
        siteName,
        externalViewer: external,
        fullPage: isFullPage,
        useUpgradePaywall: paywall,
        referralAdditionalInfo: referral,
        sameTab: selfTab,
        autoInvoke,
        t: translate
      },
      statuses: statusMap,
      actionHandlers: handlers,
      statusesCustomComponents: customComponents
    });
  }, []);
};
export const useRunDomainAction = () => {
  const _useDomainAction2 = useDomainAction(),
    executeAction = _useDomainAction2.executeAction;
  const resolveDomainAction = useResolveDomainActionCore();
  return useCallback(async (domainName, options) => {
    const _await$resolveDomainA = await resolveDomainAction(domainName, options),
      status = _await$resolveDomainA.status,
      action = _await$resolveDomainA.action;
    if (action) {
      await executeAction(action);
    }
    return {
      status
    };
  }, [resolveDomainAction, executeAction]);
};
export const useDomainActionStatus = (domainName, options) => {
  const resolveDomainAction = useResolveDomainActionCore();
  const _useDomainAction3 = useDomainAction(),
    executeAction = _useDomainAction3.executeAction;
  const _DomainSearchSuiteMod8 = DomainSearchSuiteModule.SDK.domainSearchSuiteStore,
    setIsInvokedAction = _DomainSearchSuiteMod8.setIsInvokedAction,
    useIsInvokedAction = _DomainSearchSuiteMod8.useIsInvokedAction;
  const isInvokedAction = useIsInvokedAction();
  const _useState4 = useState(),
    resolved = _useState4[0],
    setResolved = _useState4[1];
  const _useState5 = useState(false),
    loading = _useState5[0],
    setLoading = _useState5[1];
  const site = options == null ? void 0 : options.site;
  const siteId = site == null ? void 0 : site.siteId;
  const siteName = site == null ? void 0 : site.siteName;
  useEffect(() => {
    let active = true;
    if (!domainName) {
      setResolved(undefined);
      return;
    }
    setLoading(true);
    resolveDomainAction(domainName, {
      site
    }).then(result => {
      if (active) {
        setResolved(result);
      }
    }).catch(err => {
      if (active) {
        setResolved(undefined);
        console.error('Error resolving domain action:', err);
      }
    }).finally(() => {
      if (active) {
        setLoading(false);
      }
    });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domainName, siteId, siteName, resolveDomainAction]);
  const autoInvoke = resolved == null ? void 0 : resolved.autoInvoke;
  const resolvedAction = resolved == null ? void 0 : resolved.action;
  const status = resolved == null ? void 0 : resolved.status;
  useEffect(() => {
    if (autoInvoke && status && !isInvokedAction) {
      setIsInvokedAction(true);
      executeAction(resolvedAction);
    }
  }, [autoInvoke, status, resolvedAction, isInvokedAction, setIsInvokedAction, executeAction]);
  return {
    status,
    autoInvoke,
    StatusComponent: resolved == null ? void 0 : resolved.StatusComponent,
    action: () => executeAction(resolvedAction),
    loading
  };
};
//# sourceMappingURL=hooks.js.map